import { getObjectKeys } from "@avvoka/shared";
import type { PartialDeep } from "type-fest";
import { computed } from "vue";

function mergeTheme <T extends ITheme> (...objects: T[]): T {
  const isObject = (obj: string | boolean | ITheme): obj is ITheme => !!obj && typeof obj === 'object'

  return objects.reduce((prev, obj) => {
    getObjectKeys(obj).forEach(key => {
      const pVal = prev[key]
      const oVal = obj[key]

      if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeTheme(pVal, oVal)
      } else {
        prev[key] = oVal
      }
    })

    return prev
  }, {} as T)
}

/**
 * Symbol used to construct the type part of themes (what themes are available, what is the theme structure)
 */
declare const ThemeMetadata: unique symbol;

/**
 * Type to construct a declared theme. Wraps object with theme metadata
 */
type DeclaredTheme<T, ThemeKey, ThemeDefinition> = T & {
  [ThemeMetadata]: {
    ThemeKey: ThemeKey | 'default'
    ThemeDefinition: ThemeDefinition
  }
}

interface ITheme {
  [T: string]: string | boolean | ITheme
}

/**
 * Decomposes declared theme into a theme prop
 */
export type ThemeProp<T> = T extends DeclaredTheme<unknown, infer V, infer W> ? V | 'default' | W : never

export function declareTheme <const ThemeKey extends string, ThemeDefinition extends ITheme> (themes: Record<ThemeKey | 'default', ThemeDefinition>) {
  type ThemeDefinitionWithKey = ThemeDefinition & {
    /** This property is used to pass this theme to a child component as the data itself may not be compatible  */
    __key: ThemeKey | 'default'
  }

  const declaration = (props: { theme?: ThemeKey | PartialDeep<ThemeDefinition> }) => {
    const resolveTheme = (theme?: ThemeKey | PartialDeep<ThemeDefinition>) => {
      if (typeof theme === 'string') {
        return themes[theme]
      } else if (theme) {
        return mergeTheme(themes.default, theme as ThemeDefinition)
      } else {
        return themes.default
      } 
    }

    return computed<ThemeDefinitionWithKey>(() => ({
      ...resolveTheme(props.theme),
      __key: typeof props.theme === 'string' ? props.theme : 'default'
    }))
  }

  return declaration as DeclaredTheme<typeof declaration, ThemeKey, ThemeDefinition>
}